// src/hooks/useCustomTopics.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { CustomTopicModel } from "../models/CustomTopicModel";
import { customTopicService } from "../services/customTopicService";

const TOPICS_PAGE_SIZE = 20;

export type CustomTopicsSortOption = "lastEntryAt" | "recentEntryCount";

/**
 * @param autoLoad          Load the first page on mount.
 * @param showRookieEntries Viewer preference. Forwarded to the API so the badge
 *        counts (and the "most active" ordering) exclude rookie-authored entries
 *        when the viewer has them hidden — otherwise the pane advertised entries
 *        the topic page never rendered.
 */
export const useCustomTopics = (autoLoad: boolean = true, showRookieEntries: boolean = true) => {
  const [topics, setTopics]           = useState<CustomTopicModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortBy, setSortBy]           = useState<CustomTopicsSortOption>("recentEntryCount");
  // Left-pane channel ("kanal") filter — null means all channels
  const [channelFilter, setChannelFilter] = useState<number | null>(null);
  // Kept in a ref so loadTopics stays a stable useCallback while still reading the
  // current preference value (same stale-closure guard used by the entry hooks).
  const showRookieRef = useRef(showRookieEntries);
  showRookieRef.current = showRookieEntries;

  /**
   * Core loader. Pass `append=true` to add the next page to the list.
   * Pass `silent=true` to skip the loading flag so the existing list
   * stays visible during refresh (matches pane Yenile behaviour).
   */
  const loadTopics = useCallback(async (
    sort: CustomTopicsSortOption = "recentEntryCount",
    page = 0,
    append = false,
    silent = false,
    categoryId: number | null = null
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const sortDir = "desc"; // both sorts are descending (most recent / most active first)
      const result = await customTopicService.getAllTopics(page, TOPICS_PAGE_SIZE, sort, sortDir, categoryId, showRookieRef.current);
      if (append) {
        setTopics(prev => [...prev, ...result.content]);
      } else {
        setTopics(result.content);
      }
      setCurrentPage(page);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err) {
      console.error('[useCustomTopics] loadTopics error:', err);
      setError("Özel başlıklar yüklenemedi.");
    } finally {
      if (!silent) setLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  // Load on mount — skipped when autoLoad is false so the parent can lazy-load
  // this pane only when the user first switches to it.
  useEffect(() => {
    if (autoLoad) loadTopics("recentEntryCount", 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  // Reload when the viewer flips the "show rookie entries" preference, so the
  // badge counts refresh without requiring a manual Yenile.
  const isFirstPrefRun = useRef(true);
  useEffect(() => {
    if (isFirstPrefRun.current) {
      isFirstPrefRun.current = false;
      return;
    }
    loadTopics(sortBy, 0, false, true, channelFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRookieEntries]);

  const loadMoreTopics = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (isLoadingMore || nextPage >= totalPages) return;
    await loadTopics(sortBy, nextPage, true, false, channelFilter);
  }, [currentPage, totalPages, isLoadingMore, loadTopics, sortBy, channelFilter]);

  /**
   * Silent refresh — fetches fresh data without triggering the loading state,
   * so the list stays visible during the request (matches pane behaviour).
   */
  const refreshTopics = useCallback(async () => {
    await loadTopics(sortBy, 0, false, true, channelFilter);
  }, [loadTopics, sortBy, channelFilter]);

  /**
   * Shuffle ("Rastgele") — replaces the list with a random slice of active
   * topics instead of the sorted page. Deliberately silent (no loading flag)
   * so the pane does not flash, and it resets pagination: the shuffled result
   * is a single page, so totalPages comes back as 1 and "daha da..." hides.
   * The channel filter is honoured — shuffling inside a kanal stays in it.
   */
  const shuffleTopics = useCallback(async () => {
    setError(null);
    try {
      const result = await customTopicService.getRandomTopics(
        TOPICS_PAGE_SIZE, channelFilter, showRookieRef.current);
      setTopics(result.content);
      setCurrentPage(0);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err) {
      console.error('[useCustomTopics] shuffleTopics error:', err);
      setError("Özel başlıklar yüklenemedi.");
    }
  }, [channelFilter]);

  const handleSortChange = useCallback((newSort: CustomTopicsSortOption) => {
    setSortBy(newSort);
    loadTopics(newSort, 0, false, false, channelFilter);
  }, [loadTopics, channelFilter]);

  /**
   * Left-pane channel filter change — resets to page 0 and reloads.
   * Pass null to clear the filter (all channels).
   */
  const handleChannelFilterChange = useCallback((categoryId: number | null) => {
    setChannelFilter(categoryId);
    loadTopics(sortBy, 0, false, false, categoryId);
  }, [loadTopics, sortBy]);

  return {
    topics,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    isLoadingMore,
    sortBy,
    channelFilter,
    loadTopics,
    loadMoreTopics,
    refreshTopics,
    shuffleTopics,
    handleSortChange,
    handleChannelFilterChange,
  };
};